import type { VcsCallModeratorRequest } from 'api';
import { VcsMessagingControllerService } from 'api';
import { selectCallConfig, selectRoomId, selectRoomMetadata } from 'state/selectors';

export type ModeratorApiParams = {
    roomName: string;
    workspaceId: number | null | undefined;
    targetIdentity: string | null;
    targetUserId: string | null;
    jwtToken: string | null;
    isLocalGuest: boolean;
};

/**
 * Возвращает roomName и workspaceId из конфига/метаданных (для вызовов assign/remove moderator).
 */
export const getRoomNameAndWorkspaceId = (): {
    roomName: string | null;
    workspaceId: number | null | undefined;
} => {
    const callConfig = selectCallConfig();
    const roomName =
        (
            callConfig as
                | { vcsProviderConfigs?: { videoConferenceSettings?: { name?: string } }; roomName?: string }
                | undefined
        )?.vcsProviderConfigs?.videoConferenceSettings?.name ??
        callConfig?.roomName ??
        selectRoomId();
    const workspaceId = callConfig?.workspaceId ?? selectRoomMetadata()?.workspaceId;
    return { roomName, workspaceId };
};

const buildBody = (
    roomName: string,
    targetIdentity: string | null,
    targetUserId: string | null,
): VcsCallModeratorRequest & { identity?: string; jwtToken?: string } => ({
    roomName,
    identity: targetIdentity ?? undefined,
    ...(targetUserId != null && targetUserId !== '' && { userId: targetUserId }),
});

/**
 * Вызывает assignModeratorToCall.
 * В meet: при наличии conference jwtToken *OAuth (типичный путь); иначе REST с WorkspaceId.
 * `isLocalGuest` только для логов — на выбор эндпоинта не влияет.
 */
export const assignModeratorToCallApi = async (params: ModeratorApiParams): Promise<{ success: boolean }> => {
    const { roomName, workspaceId, targetIdentity, targetUserId, jwtToken, isLocalGuest } = params;
    const isValidWorkspaceId = workspaceId != null && workspaceId > 0;
    const body = buildBody(roomName, targetIdentity, targetUserId);

    if (jwtToken) {
        body.jwtToken = jwtToken;
        console.log('[Moderation] assignModeratorToCallOAuth', { isLocalGuest, workspaceId: workspaceId ?? 'нет' });
        const { error } = await VcsMessagingControllerService.assignModeratorToCallOAuth(
            body as VcsCallModeratorRequest,
        );
        if (error) {
            console.warn('[Moderation] assignModeratorToCallOAuth: ошибка', error);
            return { success: false };
        }
        return { success: true };
    }

    if (isValidWorkspaceId) {
        console.log('[Moderation] assignModeratorToCall REST', { workspaceId, body });
        const { error } = await VcsMessagingControllerService.assignModeratorToCall(
            workspaceId as number,
            body as VcsCallModeratorRequest,
        );
        if (error) {
            console.warn('[Moderation] assignModeratorToCall: ошибка', error);
            return { success: false };
        }
        return { success: true };
    }

    console.warn('[Moderation] assignModeratorToCall не отправлен: нет workspaceId и нет jwtToken для OAuth.', {
        roomName: !!roomName,
        workspaceId,
        hasJwtToken: !!jwtToken,
    });
    return { success: false };
};

/**
 * Вызывает removeModeratorFromCall (та же развилка: jwt → OAuth, иначе REST).
 */
export const removeModeratorFromCallApi = async (params: ModeratorApiParams): Promise<{ success: boolean }> => {
    const { roomName, workspaceId, targetIdentity, targetUserId, jwtToken, isLocalGuest } = params;
    const isValidWorkspaceId = workspaceId != null && workspaceId > 0;
    const body = buildBody(roomName, targetIdentity, targetUserId);

    if (jwtToken) {
        body.jwtToken = jwtToken;
        console.log('[Moderation] removeModeratorFromCallOAuth', { isLocalGuest, workspaceId: workspaceId ?? 'нет' });
        const { error } = await VcsMessagingControllerService.removeModeratorFromCallOAuth(
            body as VcsCallModeratorRequest,
        );
        if (error) {
            console.warn('[Moderation] removeModeratorFromCallOAuth ошибка', error);
            return { success: false };
        }
        return { success: true };
    }

    if (isValidWorkspaceId) {
        console.log('[Moderation] removeModeratorFromCall REST', { workspaceId, body });
        const { error } = await VcsMessagingControllerService.removeModeratorFromCall(
            workspaceId as number,
            body as VcsCallModeratorRequest,
        );
        if (error) {
            console.warn('[Moderation] removeModeratorFromCall ошибка', error);
            return { success: false };
        }
        return { success: true };
    }

    console.warn('[Moderation] removeModeratorFromCall не отправлен: нет workspaceId и нет jwtToken для OAuth.', {
        roomName: !!roomName,
        workspaceId,
        hasJwtToken: !!jwtToken,
    });
    return { success: false };
};
